import { useCallback, useMemo } from 'react';
import { CommentOutlined, CopyOutlined, StopOutlined } from '@ant-design/icons';
import { Empty, Space, Spin, Tag, message, MenuProps, Pagination } from 'antd';
import { MenuInfo } from 'rc-menu/lib/interface';
import { useNavigate, useParams } from 'react-router-dom';
import { ProcState } from '@/entities/proc';
import {
  ProcDetailsResponse,
  ProcInstancesResponse,
  UserProcResponse,
  useAdministrateUserProc,
  useDeleteProcInstance,
  useProcDetails,
  useProcInstances,
} from '@/shared/api';
import { usePaging } from '@/shared/hooks';
import { useThemeContext, themeConfig } from '@/shared/theme';
import { Col, ContextMenu, Table } from '@/shared/ui';
import { captureError } from '@/shared/utils/error-reporting';
import { PageLayout } from '@/widgets/page-layout';
import { ProcInstancesPageHeader } from './ProcInstancesPageHeader';
import EmptyIcon from '@assets/empty.svg';

enum MenuOptions {
  CopyInstanceLink = 'copyInstanceLink',
  ToggleComments = 'toggleComments',
  Suspend = 'suspend',
  Delete = 'delete',
}

const UserProcStateField = ({ state }: { state: ProcState }) => {
  switch (state) {
    case 'active':
      return <Tag color="blue">Active</Tag>;
    case 'suspended':
      return <Tag color="red">Suspended</Tag>;
    case 'completed':
      return <Tag color="green">Completed</Tag>;
    case 'deprecated':
      return <Tag color="black">Deprecated</Tag>;

    default:
      return <Tag>Instantiated</Tag>;
  }
};

export const ProcInstancesPage = () => {
  const { procId = '' } = useParams();
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const { page, pageSize, pageSizeOptions, onPagingChange } = usePaging();
  const { data: procData } = useProcDetails<ProcDetailsResponse>({
    pathParams: { id: procId },
  });
  const {
    data: procInstances,
    isLoading: procInstancesLoading,
    refetch: refetchInstances,
  } = useProcInstances<ProcInstancesResponse>({
    pathParams: { procId },
    queryParams: { page, take: pageSize },
  });
  const { mutate: updateInstance } = useAdministrateUserProc();
  const { mutate: deleteInstance } = useDeleteProcInstance();

  const onToggleComments = useCallback(
    (instanceProc: UserProcResponse) => {
      updateInstance(
        {
          pathParams: { id: procId!, instanceId: instanceProc.id },
          body: {
            commentsEnabled:
              !instanceProc.stepData.administration.commentsEnabled,
          },
        },
        { onSuccess: refetchInstances },
      );
    },
    [procId, refetchInstances, updateInstance],
  );

  const onSuspend = useCallback(
    (instanceProc: UserProcResponse) => {
      const state = instanceProc.state;
      updateInstance(
        {
          pathParams: { id: procId!, instanceId: instanceProc.id },
          body: { state: state === 'suspended' ? 'active' : 'suspended' },
        },
        { onSuccess: refetchInstances },
      );
    },
    [procId, refetchInstances, updateInstance],
  );

  const handleInstanceRemove = useCallback(
    (instance: UserProcResponse) => {
      deleteInstance(
        { pathParams: { id: procId!, instanceId: instance.id } },
        {
          onSuccess: refetchInstances,
          onError: (error) => {
            captureError(error, {
              module: 'proc_instances_view_on_error_handle_instance_remove',
              action: 'handle_instance_remove',
            });
          },
        },
      );
    },
    [deleteInstance, procId, refetchInstances],
  );

  const onContextItemClick = useCallback(
    ({ key }: MenuInfo, instanceProc: UserProcResponse) => {
      switch (key) {
        case MenuOptions.CopyInstanceLink:
          message.success('Link copied to clipboard');
          return navigator.clipboard.writeText(
            `${import.meta.env.VITE_BASE_URL}/procs/${instanceProc.id}/${
              instanceProc.owner.code
            }`,
          );
        case MenuOptions.ToggleComments:
          return onToggleComments(instanceProc);
        case MenuOptions.Suspend:
          return onSuspend(instanceProc);
        case MenuOptions.Delete:
          return handleInstanceRemove(instanceProc);
        default:
          return null;
      }
    },
    [handleInstanceRemove, onSuspend, onToggleComments],
  );

  const contextMenuOptions = useCallback(
    (instance: UserProcResponse): MenuProps['items'] => [
      {
        label: (
          <Space>
            <CopyOutlined />
            Copy instance link
          </Space>
        ),
        key: MenuOptions.CopyInstanceLink,
      },
      {
        label: (
          <Space>
            <CommentOutlined />
            {instance.stepData.administration.commentsEnabled
              ? 'Disable comments'
              : 'Enable comments'}
          </Space>
        ),
        key: MenuOptions.ToggleComments,
      },
      {
        label: (
          <Space>
            <StopOutlined />
            {instance.state === 'suspended' ? 'Activate proc' : 'Suspend proc'}
          </Space>
        ),
        key: MenuOptions.Suspend,
      },
      { type: 'divider' },
      {
        label: (
          <Space>
            <StopOutlined />
            Delete instance
          </Space>
        ),
        key: MenuOptions.Delete,
        style: { color: themeConfig[theme].colors.error },
      },
    ],
    [theme],
  );

  const columns = useMemo(
    () => [
      {
        title: 'User email',
        dataIndex: 'email',
        filterSearch: true,
        key: 'email',
        render: (_: unknown, record: UserProcResponse) => (
          <span>{record.owner.email}</span>
        ),
      },
      {
        title: 'State',
        dataIndex: 'state',
        key: 'state',
        render: (_: unknown, record: UserProcResponse) => (
          <UserProcStateField state={record.state} />
        ),
      },
      {
        title: 'Last updated step',
        dataIndex: 'lastUpdatedStep',
        key: 'lastUpdatedStep',
        render: (_, record) => {
          const stepIndex =
            record.stepData.steps.findIndex(
              (step) => step.id === record.lastUpdated?.step?.id,
            ) + 1;
          return (
            <span>
              {record.lastUpdated?.step?.title
                ? `#${stepIndex} ${record.lastUpdated?.step?.title}`
                : '-'}
            </span>
          );
        },
      },
      {
        title: 'Updated at',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: (_, record) => (
          <span>
            {record.lastUpdated?.step?.at
              ? Intl.DateTimeFormat('en-US', {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                }).format(new Date(record.lastUpdated?.step?.at))
              : '-'}
          </span>
        ),
      },
      {
        title: 'Action',
        dataIndex: 'operation',
        key: 'operation',
        render: (_, record) => (
          <div style={{ width: 'fit-content' }}>
            <ContextMenu
              options={contextMenuOptions(record)}
              onClick={(info) => onContextItemClick(info, record)}
            />
          </div>
        ),
      },
    ],
    [contextMenuOptions, onContextItemClick],
  );

  return (
    <PageLayout
      headerContent={<ProcInstancesPageHeader proc={procData} />}
      contentStyle={{ background: themeConfig[theme].colors.bgBase }}
    >
      <Col
        justifyContent="flex-start"
        style={{
          position: 'relative',
          height: '100%',
        }}
      >
        {procInstancesLoading ? (
          <Spin />
        ) : procInstances?.data.length === 0 ? (
          <Empty
            image={EmptyIcon}
            styles={{ image: { height: '25%', marginTop: '10%' } }}
            description={<span>There are no instances of this proc</span>}
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          />
        ) : (
          <>
            <Table
              columns={columns}
              dataSource={procInstances?.data}
              loading={procInstancesLoading}
              rowKey={(record: UserProcResponse) => record.id}
              onRow={(record: UserProcResponse) => ({
                onClick: () =>
                  navigate(`/dashboard/author/proc/instance/${record.id}`),
              })}
              style={{ width: '100%' }}
              pagination={false}
            />
            <Pagination
              hideOnSinglePage
              showSizeChanger
              disabled={procInstancesLoading}
              total={procInstances?.meta.itemCount}
              current={page}
              pageSize={pageSize}
              pageSizeOptions={pageSizeOptions}
              onChange={onPagingChange}
            />
          </>
        )}
      </Col>
    </PageLayout>
  );
};
